import { useLanguage } from '../context/LanguageContext';
import React, { useState, useEffect } from 'react';
import { useAuth, apiClient, formatApiError } from '../context/AuthContext';
import { User, Mail, Phone, MapPin, Building2, Lock, Eye, EyeOff, Save, AlertCircle, Globe, FileText, Briefcase } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'password'
  
  // Profile form
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    // Shipping address
    shipping_address: '',
    shipping_postal_code: '',
    shipping_city: '',
    shipping_country: 'France',
    // Billing address (optional)
    use_different_billing: false,
    billing_first_name: '',
    billing_last_name: '',
    billing_company: '',
    billing_address: '',
    billing_postal_code: '',
    billing_city: '',
    billing_country: 'France',
    company_name: '' // Read-only for Pro users
  });

  // Password form
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Pro company info (read-only)
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'pro') {
      setCompanyInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get('/profile/company');
        if (!cancelled) setCompanyInfo(data);
      } catch (err) {
        console.error('Error loading company info', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (user) {
      // Split name into firstName and lastName
      const nameParts = (user.name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      setProfileForm({
        firstName,
        lastName,
        email: user.email || '',
        phone: user.phone || '',
        shipping_address: user.shipping_address || '',
        shipping_postal_code: user.shipping_postal_code || '',
        shipping_city: user.shipping_city || '',
        shipping_country: user.shipping_country || 'France',
        use_different_billing: user.use_different_billing || false,
        billing_first_name: user.billing_first_name || '',
        billing_last_name: user.billing_last_name || '',
        billing_company: user.billing_company || '',
        billing_address: user.billing_address || '',
        billing_postal_code: user.billing_postal_code || '',
        billing_city: user.billing_city || '',
        billing_country: user.billing_country || 'France',
        company_name: user.company_name || ''
      });
    }
  }, [user]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const fullName = `${profileForm.firstName} ${profileForm.lastName}`.trim();
      const updateData = {
        name: fullName,
        email: profileForm.email,
        phone: profileForm.phone,
        shipping_address: profileForm.shipping_address,
        shipping_postal_code: profileForm.shipping_postal_code,
        shipping_city: profileForm.shipping_city,
        shipping_country: profileForm.shipping_country,
        use_different_billing: profileForm.use_different_billing,
        billing_first_name: profileForm.billing_first_name,
        billing_last_name: profileForm.billing_last_name,
        billing_company: profileForm.billing_company,
        billing_address: profileForm.billing_address,
        billing_postal_code: profileForm.billing_postal_code,
        billing_city: profileForm.billing_city,
        billing_country: profileForm.billing_country
        // company_name is intentionally NOT sent (cannot be modified)
      };

      const { data } = await apiClient.put('/profile', updateData);
      setUser(data.user); // Update user in context
      toast.success('Profil mis à jour avec succès');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // Validation
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordForm.new_password.length < 6) {
      toast.error('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);

    try {
      await apiClient.post('/profile/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });

      toast.success('Mot de passe modifié avec succès');
      
      // Reset form
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const isPro = user?.role === 'pro' || user?.role === 'admin';

  return (
    <div className="min-h-screen bg-[#f5f7fa] py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-3xl border border-gray-100 p-8 mb-6 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-[#00aaff] to-[#0088dd] rounded-full flex items-center justify-center">
              <User size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black text-gray-900">{t("profile.title")}</h1>
              <p className="text-gray-500">Gérez vos informations personnelles</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex border-b border-gray-100">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex-1 px-6 py-4 font-bold transition-colors ${
                activeTab === 'profile'
                  ? 'bg-[#00aaff] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <User size={20} />
                Informations personnelles
              </div>
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`flex-1 px-6 py-4 font-bold transition-colors ${
                activeTab === 'password'
                  ? 'bg-[#00aaff] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Lock size={20} />
                Mot de passe
              </div>
            </button>
          </div>

          <div className="p-8">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <form onSubmit={handleProfileUpdate} className="space-y-6">
                {/* First Name & Last Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                      Prénom *
                    </label>
                    <div className="relative">
                      <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={profileForm.firstName}
                        onChange={(e) => setProfileForm({ ...profileForm, firstName: e.target.value })}
                        placeholder="Jean"
                        required
                        className="w-full border border-gray-200 rounded-xl pl-12 pr-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                      Nom *
                    </label>
                    <div className="relative">
                      <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={profileForm.lastName}
                        onChange={(e) => setProfileForm({ ...profileForm, lastName: e.target.value })}
                        placeholder="Dupont"
                        required
                        className="w-full border border-gray-200 rounded-xl pl-12 pr-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                      />
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                      placeholder="jean@example.com"
                      required
                      className="w-full border border-gray-200 rounded-xl pl-12 pr-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                    Téléphone
                  </label>
                  <div className="relative">
                    <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      placeholder="06 00 00 00 00"
                      className="w-full border border-gray-200 rounded-xl pl-12 pr-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                    />
                  </div>
                </div>

                {/* Shipping Address Section */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin size={20} className="text-[#00aaff]" />
                    Adresse de livraison
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Shipping Address */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                        Adresse
                      </label>
                      <input
                        type="text"
                        value={profileForm.shipping_address}
                        onChange={(e) => setProfileForm({ ...profileForm, shipping_address: e.target.value })}
                        placeholder="10 rue de Paris"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                      />
                    </div>

                    {/* Postal Code & City */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                          Code postal
                        </label>
                        <input
                          type="text"
                          value={profileForm.shipping_postal_code}
                          onChange={(e) => setProfileForm({ ...profileForm, shipping_postal_code: e.target.value })}
                          placeholder="75001"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                          Ville
                        </label>
                        <input
                          type="text"
                          value={profileForm.shipping_city}
                          onChange={(e) => setProfileForm({ ...profileForm, shipping_city: e.target.value })}
                          placeholder="Paris"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                        />
                      </div>
                    </div>

                    {/* Country */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                        Pays
                      </label>
                      <input
                        type="text"
                        value={profileForm.shipping_country}
                        onChange={(e) => setProfileForm({ ...profileForm, shipping_country: e.target.value })}
                        placeholder="France"
                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                      />
                    </div>
                  </div>
                </div>

                {/* Billing Address Toggle */}
                <div className="border-t pt-6">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={profileForm.use_different_billing}
                        onChange={(e) => setProfileForm({ ...profileForm, use_different_billing: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#00aaff]"></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-700 group-hover:text-[#00aaff] transition-colors">
                      Utiliser une adresse de facturation différente
                    </span>
                  </label>

                  {/* Billing Address Fields (shown when toggle is ON) */}
                  {profileForm.use_different_billing && (
                    <div className="mt-6 space-y-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Adresse de facturation</h4>
                      
                      {/* Billing First Name & Last Name */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                            Prénom
                          </label>
                          <input
                            type="text"
                            value={profileForm.billing_first_name}
                            onChange={(e) => setProfileForm({ ...profileForm, billing_first_name: e.target.value })}
                            placeholder="Jean"
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                            Nom
                          </label>
                          <input
                            type="text"
                            value={profileForm.billing_last_name}
                            onChange={(e) => setProfileForm({ ...profileForm, billing_last_name: e.target.value })}
                            placeholder="Dupont"
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors bg-white"
                          />
                        </div>
                      </div>

                      {/* Billing Company */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                          Société (optionnel)
                        </label>
                        <input
                          type="text"
                          value={profileForm.billing_company}
                          onChange={(e) => setProfileForm({ ...profileForm, billing_company: e.target.value })}
                          placeholder="Mon Entreprise SARL"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors bg-white"
                        />
                      </div>

                      {/* Billing Address */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                          Adresse
                        </label>
                        <input
                          type="text"
                          value={profileForm.billing_address}
                          onChange={(e) => setProfileForm({ ...profileForm, billing_address: e.target.value })}
                          placeholder="10 rue de Paris"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors bg-white"
                        />
                      </div>

                      {/* Billing Postal Code & City */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                            Code postal
                          </label>
                          <input
                            type="text"
                            value={profileForm.billing_postal_code}
                            onChange={(e) => setProfileForm({ ...profileForm, billing_postal_code: e.target.value })}
                            placeholder="75001"
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors bg-white"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                            Ville
                          </label>
                          <input
                            type="text"
                            value={profileForm.billing_city}
                            onChange={(e) => setProfileForm({ ...profileForm, billing_city: e.target.value })}
                            placeholder="Paris"
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors bg-white"
                          />
                        </div>
                      </div>

                      {/* Billing Country */}
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                          Pays
                        </label>
                        <input
                          type="text"
                          value={profileForm.billing_country}
                          onChange={(e) => setProfileForm({ ...profileForm, billing_country: e.target.value })}
                          placeholder="France"
                          className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors bg-white"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Company Info (Read-only for Pro users) */}
                {isPro && companyInfo && (
                  <div className="border-t border-gray-100 pt-6 mt-2" data-testid="pro-company-info-section">
                    <div className="flex items-center gap-2 mb-4">
                      <Building2 size={20} className="text-[#00aaff]" />
                      <h3 className="text-base font-bold text-gray-900">Informations société</h3>
                      <span className="ml-2 bg-[#00aaff]/10 text-[#00aaff] text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">Pro</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Raison sociale */}
                      <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                          Raison sociale
                        </label>
                        <div className="relative">
                          <Building2 size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            data-testid="pro-company-name"
                            value={companyInfo.company_name || ''}
                            disabled
                            className="w-full border border-gray-200 rounded-xl pl-12 pr-4 py-3 text-sm outline-none bg-gray-50 text-gray-700 cursor-not-allowed"
                          />
                        </div>
                      </div>

                      {/* SIRET */}
                      {companyInfo.siret && (
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                            SIRET
                          </label>
                          <div className="relative">
                            <FileText size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              data-testid="pro-company-siret"
                              value={companyInfo.siret}
                              disabled
                              className="w-full border border-gray-200 rounded-xl pl-12 pr-4 py-3 text-sm outline-none bg-gray-50 text-gray-700 cursor-not-allowed"
                            />
                          </div>
                        </div>
                      )}

                      {/* Activité */}
                      {companyInfo.activity && (
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                            Activité
                          </label>
                          <div className="relative">
                            <Briefcase size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              data-testid="pro-company-activity"
                              value={companyInfo.activity}
                              disabled
                              className="w-full border border-gray-200 rounded-xl pl-12 pr-4 py-3 text-sm outline-none bg-gray-50 text-gray-700 cursor-not-allowed"
                            />
                          </div>
                        </div>
                      )}

                      {/* Site web */}
                      {companyInfo.website && (
                        <div className="md:col-span-2">
                          <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                            Site web
                          </label>
                          <div className="relative">
                            <Globe size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              data-testid="pro-company-website"
                              value={companyInfo.website}
                              disabled
                              className="w-full border border-gray-200 rounded-xl pl-12 pr-4 py-3 text-sm outline-none bg-gray-50 text-gray-700 cursor-not-allowed"
                            />
                          </div>
                        </div>
                      )}

                      {/* Contact pro */}
                      {companyInfo.contact_name && (
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                            Contact
                          </label>
                          <div className="relative">
                            <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              data-testid="pro-company-contact"
                              value={companyInfo.contact_name}
                              disabled
                              className="w-full border border-gray-200 rounded-xl pl-12 pr-4 py-3 text-sm outline-none bg-gray-50 text-gray-700 cursor-not-allowed"
                            />
                          </div>
                        </div>
                      )}

                      {/* Téléphone pro */}
                      {companyInfo.phone && (
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                            Téléphone pro
                          </label>
                          <div className="relative">
                            <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              data-testid="pro-company-phone"
                              value={companyInfo.phone}
                              disabled
                              className="w-full border border-gray-200 rounded-xl pl-12 pr-4 py-3 text-sm outline-none bg-gray-50 text-gray-700 cursor-not-allowed"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <AlertCircle size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-blue-700">
                        <strong>Information :</strong> Ces informations sont en lecture seule. Pour toute modification,
                        contactez-nous à <a href="mailto:contact@joeycyan.com" className="underline font-semibold">contact@joeycyan.com</a>.
                      </p>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#00aaff] text-white py-3.5 rounded-full font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#0088dd] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save size={18} />
                      Enregistrer les modifications
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Password Tab */}
            {activeTab === 'password' && (
              <form onSubmit={handlePasswordChange} className="space-y-6">
                {/* Current Password */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                    Mot de passe actuel *
                  </label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                      placeholder="••••••••"
                      required
                      className="w-full border border-gray-200 rounded-xl pl-12 pr-12 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                    Nouveau mot de passe *
                  </label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className="w-full border border-gray-200 rounded-xl pl-12 pr-12 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">Minimum 6 caractères</p>
                </div>

                {/* Confirm New Password */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">
                    Confirmer le nouveau mot de passe *
                  </label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className="w-full border border-gray-200 rounded-xl pl-12 pr-12 py-3 text-sm outline-none focus:border-[#00aaff] transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#00aaff] text-white py-3.5 rounded-full font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#0088dd] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Lock size={18} />
                      Modifier le mot de passe
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
